import { useMemo, useState } from 'react'
import { ClientPosition } from './ClientPosition'
import { LaneLedger } from './LaneLedger'
import { QuoteEconomics } from './QuoteEconomics'

interface CustomerLane {
  id: string
  route: string
  equipment: string
  shipments: number
  lastSell: number
  market: number
  margin: number
  history: number[]
  need: string
}

interface CustomerAccount {
  id: string
  name: string
  /** Where offers for this customer are sent. */
  email: string
  initials: string
  terms: string
  credit: number
  creditUsed: number
  openQuotes: number
  winRate: number
  annualValue: number
  lanes: CustomerLane[]
}

const CUSTOMERS: CustomerAccount[] = [
  {
    id: 'northstar',
    name: 'Northstar Projects',
    email: 'ops@northstarprojects.com',
    initials: 'NP',
    terms: '30 days',
    credit: 120000,
    creditUsed: 48500,
    openQuotes: 3,
    winRate: 64,
    annualValue: 286000,
    lanes: [
      {
        id: 'sha-rtm',
        route: 'CNSHA → NLRTM',
        equipment: '40HC',
        shipments: 18,
        lastSell: 3878,
        market: 3660,
        margin: 18,
        history: [3380, 3290, 3410, 3520, 3460, 3660],
        need: 'Weekly production cargo · price stability preferred',
      },
      {
        id: 'ngb-nyc',
        route: 'CNNGB → USNYC',
        equipment: '40HC',
        shipments: 9,
        lastSell: 5253,
        market: 4930,
        margin: 21,
        history: [4580, 4720, 4630, 4810, 4880, 4930],
        need: 'Twice monthly · reliable transit is critical',
      },
      {
        id: 'szx-fxt',
        route: 'CNSZX → GBFXT',
        equipment: '40HC',
        shipments: 7,
        lastSell: 3720,
        market: 3510,
        margin: 16,
        history: [3670, 3610, 3550, 3480, 3440, 3510],
        need: 'Monthly replenishment · flexible sailing window',
      },
    ],
  },
  {
    id: 'aster',
    name: 'Aster Machinery',
    email: 'ops@astermachinery.com',
    initials: 'AM',
    terms: '21 days',
    credit: 90000,
    creditUsed: 71200,
    openQuotes: 2,
    winRate: 48,
    annualValue: 194000,
    lanes: [
      {
        id: 'mun-ham',
        route: 'INMUN → DEHAM',
        equipment: "40' FR",
        shipments: 6,
        lastSell: 15470,
        market: 14350,
        margin: 17,
        history: [13100, 13480, 13720, 13940, 14100, 14350],
        need: 'ODC machinery · engineering approval required',
      },
      {
        id: 'nsa-rtm',
        route: 'INNSA → NLRTM',
        equipment: "40' OT",
        shipments: 4,
        lastSell: 11980,
        market: 11340,
        margin: 14,
        history: [10700, 10850, 11220, 11080, 11190, 11340],
        need: 'ODC machinery · top-load and survey required',
      },
    ],
  },
  {
    id: 'hearth',
    name: 'Hearth & Field',
    email: 'buying@hearthandfield.com',
    initials: 'HF',
    terms: '14 days',
    credit: 65000,
    creditUsed: 22400,
    openQuotes: 1,
    winRate: 71,
    annualValue: 168000,
    lanes: [
      {
        id: 'sgn-fxt',
        route: 'VNSGN → GBFXT',
        equipment: '40HC',
        shipments: 22,
        lastSell: 3352,
        market: 3180,
        margin: 9,
        history: [3010, 2960, 3070, 3110, 3150, 3180],
        need: 'Weekly retail stock · fast confirmation preferred',
      },
      {
        id: 'sgn-lax',
        route: 'VNSGN → USLAX',
        equipment: '40HC',
        shipments: 12,
        lastSell: 3490,
        market: 3260,
        margin: 15,
        history: [3460, 3390, 3310, 3270, 3210, 3260],
        need: 'Fortnightly stock · lowest landed cost preferred',
      },
    ],
  },
]

const usd = (n: number) => `$${n.toLocaleString('en-US')}`

export function CustomerQuotes() {
  const [customerId, setCustomerId] = useState(CUSTOMERS[0]?.id ?? '')
  const customer = CUSTOMERS.find((item) => item.id === customerId) ?? CUSTOMERS[0]
  const [laneId, setLaneId] = useState(customer?.lanes[0]?.id ?? '')
  const [laneSort, setLaneSort] = useState<'activity' | 'lane' | 'value'>('activity')
  const [reused, setReused] = useState<{
    ref: string
    laneId: string
    oldBuy: number
    oldSell: number
  } | null>(null)
  const lane = customer?.lanes.find((item) => item.id === laneId) ?? customer?.lanes[0]

  const chooseCustomer = (id: string) => {
    const next = CUSTOMERS.find((item) => item.id === id)
    setCustomerId(id)
    setLaneId(next?.lanes[0]?.id ?? '')
    setReused(null)
  }

  if (!customer || !lane) return null
  const currentVsMarket = Math.round(((lane.lastSell - lane.market) / lane.market) * 100)
  const netBuy = Math.round(lane.market * 0.97)
  const earnedProfit = lane.lastSell - netBuy
  const sortedLanes = useMemo(() => {
    const lanes = [...customer.lanes]
    if (laneSort === 'lane') return lanes.sort((a, b) => a.route.localeCompare(b.route))
    if (laneSort === 'value') return lanes.sort((a, b) => b.lastSell - a.lastSell)
    return lanes.sort((a, b) => b.shipments - a.shipments)
  }, [customer.lanes, laneSort])
  const options = [
    {
      source: 'Your line contract',
      provider: 'MSC',
      amount: Math.round(lane.market * 0.97),
      detail: 'Direct · allocation available',
      tone: '#2563eb',
      bookable: true,
    },
    {
      source: 'Partner rate',
      provider: 'SeaBridge',
      amount: Math.round(lane.market * 1.015),
      detail: 'Co-loader · 14 days credit',
      tone: '#8b5cf6',
      bookable: true,
    },
    {
      source: 'Market benchmark',
      provider: 'Navo Index',
      amount: lane.market,
      detail: 'Context only · not bookable',
      tone: '#e5556b',
      bookable: false,
    },
  ]
  const recentShipments = customer.lanes
    .flatMap((item, laneIndex) => [
      {
        ref: `SJK-${customer.id.slice(0, 2).toUpperCase()}-${8042 - laneIndex * 17}`,
        lane: item,
        date: laneIndex === 0 ? '28 Sep 2026' : '12 Sep 2026',
        status: 'Delivered',
      },
      ...(laneIndex === 0
        ? [
            {
              ref: `SJK-${customer.id.slice(0, 2).toUpperCase()}-7981`,
              lane: item,
              date: '19 Aug 2026',
              status: 'Completed',
            },
          ]
        : []),
    ])
    .slice(0, 3)

  const reuseShipment = (shipment: (typeof recentShipments)[number]) => {
    const oldBuy = Math.round(shipment.lane.market * 0.94)
    setLaneId(shipment.lane.id)
    setReused({
      ref: shipment.ref,
      laneId: shipment.lane.id,
      oldBuy,
      oldSell: shipment.lane.lastSell,
    })
  }

  return (
    <section className="fcq" aria-label="Customer quote workspace">
      <style>{styles}</style>
      <header className="fcq-head">
        <div>
          <span>Customer quote workspace</span>
          <h2>Start with the client. Build around their need.</h2>
          <p>Choose the account first; lanes, rates, history and actions follow.</p>
        </div>
        <label>
          <small>Customer</small>
          <select
            aria-label="Choose customer"
            value={customerId}
            onChange={(event) => chooseCustomer(event.target.value)}
          >
            {CUSTOMERS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </header>

      <ClientPosition
        key={customer.id}
        id={customer.id}
        name={customer.name}
        initials={customer.initials}
        terms={customer.terms}
        credit={customer.credit}
        creditUsed={customer.creditUsed}
        annualValue={customer.annualValue}
      />

      <section className="fcq-recent">
        <header>
          <div>
            <span>Recent shipment history</span>
            <h3>Reuse what already worked.</h3>
          </div>
          <button type="button">View all history →</button>
        </header>
        <div>
          {recentShipments.map((shipment) => {
            const oldBuy = Math.round(shipment.lane.market * 0.94)
            return (
              <article key={shipment.ref}>
                <div>
                  <small>
                    {shipment.date} · {shipment.status}
                  </small>
                  <b>{shipment.lane.route}</b>
                  <span>
                    {shipment.ref} · {shipment.lane.equipment}
                  </span>
                </div>
                <div>
                  <small>Last sell</small>
                  <strong>{usd(shipment.lane.lastSell)}</strong>
                </div>
                <div className="profit">
                  <small>Profit</small>
                  <strong>{usd(shipment.lane.lastSell - oldBuy)}</strong>
                </div>
                <button type="button" onClick={() => reuseShipment(shipment)}>
                  Reuse draft ↻
                </button>
              </article>
            )
          })}
        </div>
      </section>

      <div className="fcq-layout">
        <aside className="fcq-lanes">
          <header className="fcq-lanes-head">
            <div>
              <b>Client lanes</b>
              <span>{customer.lanes.length} active</span>
            </div>
            <select
              aria-label="Sort client lanes"
              value={laneSort}
              onChange={(event) => setLaneSort(event.target.value as typeof laneSort)}
            >
              <option value="activity">Most used</option>
              <option value="lane">Lane A–Z</option>
              <option value="value">Highest value</option>
            </select>
          </header>
          {sortedLanes.map((item) => {
            const delta = Math.round(((item.lastSell - item.market) / item.market) * 100)
            return (
              <button
                type="button"
                key={item.id}
                className={lane.id === item.id ? 'active' : ''}
                onClick={() => {
                  setLaneId(item.id)
                  setReused(null)
                }}
              >
                <div>
                  <b>{item.route}</b>
                  <span>
                    {item.equipment} · {item.shipments} shipments
                  </span>
                </div>
                <div>
                  <strong>{usd(item.lastSell)}</strong>
                  <em>{usd(item.lastSell - Math.round(item.market * 0.97))} profit</em>
                  <small className={delta > 6 ? 'warm' : ''}>
                    {delta > 0 ? '+' : ''}
                    {delta}% vs market
                  </small>
                </div>
              </button>
            )
          })}
          <button type="button" className="fcq-new-lane">
            ＋ Add customer lane
          </button>
        </aside>

        <main className="fcq-main">
          <div className="fcq-lane-head">
            <div>
              <span>{lane.equipment} · selected lane</span>
              <h3>{lane.route}</h3>
              <p>{lane.need}</p>
            </div>
            <button type="button">Create quote →</button>
          </div>

          <div className="fcq-economics">
            <Metric
              label="Last selling rate"
              value={usd(lane.lastSell)}
              note="charged to this client"
            />
            <Metric label="Net buying rate" value={usd(netBuy)} note="best usable rate now" />
            <Metric
              label="Profit per shipment"
              value={usd(earnedProfit)}
              note={`${Math.round((earnedProfit / lane.lastSell) * 100)}% realised margin`}
            />
            <Metric
              label="Historic lane profit"
              value={usd(earnedProfit * lane.shipments)}
              note={`${lane.shipments} completed shipments`}
            />
          </div>

          <LaneLedger
            route={lane.route}
            equipment={lane.equipment}
            sell={lane.lastSell}
            buy={netBuy}
          />

          <div className="fcq-insights">
            <section className="fcq-chart-card">
              <header>
                <div>
                  <span>Market rate history</span>
                  <strong>{usd(lane.market)}</strong>
                </div>
                <small>6 months · {lane.equipment}</small>
              </header>
              <HistoryChart values={lane.history} />
              <footer>
                <span>May</span>
                <span>Jun</span>
                <span>Jul</span>
                <span>Aug</span>
                <span>Sep</span>
                <span>Oct</span>
              </footer>
            </section>
            <section className="fcq-position-card">
              <span>Customer position</span>
              <strong>
                {currentVsMarket > 0 ? '+' : ''}
                {currentVsMarket}%
              </strong>
              <p>Last sell is {currentVsMarket}% above today’s market.</p>
              <div>
                <span>
                  Last sell <b>{usd(lane.lastSell)}</b>
                </span>
                <span>
                  Margin <b>{lane.margin}%</b>
                </span>
              </div>
              <button type="button">Reprice this lane →</button>
            </section>
          </div>

          <section className="fcq-options">
            <header>
              <div>
                <span>Available now</span>
                <h3>Choose the right rate source.</h3>
              </div>
              <small>Compared on the same {lane.equipment} basis</small>
            </header>
            <div>
              {options.map((option, index) => (
                <article key={option.source} className={index === 0 ? 'best' : ''}>
                  <i style={{ background: option.tone }} />
                  <div>
                    <span>{option.source}</span>
                    <b>{option.provider}</b>
                    <small>{option.detail}</small>
                  </div>
                  <strong>{usd(option.amount)}</strong>
                  {option.bookable ? (
                    <button type="button">Use rate →</button>
                  ) : (
                    <button type="button" className="quiet">
                      View market →
                    </button>
                  )}
                </article>
              ))}
            </div>
          </section>

          <QuoteEconomics
            key={`${lane.id}-${reused?.ref ?? 'new'}`}
            route={lane.route}
            equipment={lane.equipment}
            freightBuy={netBuy}
            freightSell={
              reused?.laneId === lane.id ? netBuy + (reused.oldSell - reused.oldBuy) : netBuy
            }
            reusedFrom={reused?.laneId === lane.id ? reused.ref : undefined}
            customer={{ name: customer.name, email: customer.email }}
          />

          <section className="fcq-actions">
            <header>
              <b>Action points</b>
              <span>3 recommendations</span>
            </header>
            <Action
              tone="#e5556b"
              title="Reprice before sending"
              detail={`The last sell is ${currentVsMarket}% above market; protect the margin without losing the account.`}
              action="Open draft"
            />
            <Action
              tone="#f59e0b"
              title="Confirm allocation"
              detail="The best contract rate has indicative allocation. Confirm space before promising the sailing."
              action="Ask carrier"
            />
            <Action
              tone="#10b981"
              title="Use customer history"
              detail={`${lane.shipments} prior shipments give enough evidence to prepare the offer with confidence.`}
              action="Prepare offer"
            />
          </section>
        </main>
      </div>
    </section>
  )
}

function Metric({
  label,
  value,
  note,
  warn = false,
}: { label: string; value: string; note: string; warn?: boolean }) {
  return (
    <div className="fcq-metric">
      <span>{label}</span>
      <strong className={warn ? 'warn' : ''}>{value}</strong>
      <small>{note}</small>
    </div>
  )
}

function Action({
  tone,
  title,
  detail,
  action,
}: { tone: string; title: string; detail: string; action: string }) {
  return (
    <div className="fcq-action">
      <i style={{ background: tone }} />
      <div>
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <button type="button">{action} →</button>
    </div>
  )
}

function HistoryChart({ values }: { values: number[] }) {
  const points = useMemo(() => {
    const min = Math.min(...values) * 0.98
    const max = Math.max(...values) * 1.02
    const range = max - min || 1
    return values
      .map(
        (value, index) =>
          `${(index / (values.length - 1)) * 100},${90 - ((value - min) / range) * 72}`
      )
      .join(' ')
  }, [values])
  return (
    <svg
      className="fcq-chart"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label="Six month market rate history"
    >
      <defs>
        <linearGradient id="fcq-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e5556b" stopOpacity=".2" />
          <stop offset="1" stopColor="#e5556b" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,100 ${points} 100,100`} fill="url(#fcq-fill)" stroke="none" />
      <polyline
        points={points}
        fill="none"
        stroke="#e5556b"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx="100"
        cy={points.split(' ').at(-1)?.split(',')[1]}
        r="2.3"
        fill="#e5556b"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

const styles = `
.fcq{padding-top:24px}.fcq *{box-sizing:border-box}.fcq button,.fcq select{font:inherit}.fcq-head{display:flex;align-items:flex-end;justify-content:space-between;gap:24px}.fcq-head>div>span,.fcq-lane-head span,.fcq-chart-card header span,.fcq-position-card>span,.fcq-options header span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;letter-spacing:.13em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.fcq-head h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:27px;letter-spacing:-.028em;margin:6px 0 3px}.fcq-head p{margin:0;color:var(--text-muted);font-size:13px}.fcq-head label{display:grid;gap:5px;min-width:230px}.fcq-head label small{font-family:var(--mono,ui-monospace,monospace);font-size:8px;text-transform:uppercase;letter-spacing:.1em;color:var(--text-muted)}.fcq-head select{border:1px solid var(--border);border-radius:10px;background:var(--surface-0);color:inherit;padding:10px 12px;font-weight:650}.fcq-account{display:grid;grid-template-columns:1.45fr repeat(3,1fr);border:1px solid var(--border);background:var(--surface-0);border-radius:15px;margin-top:18px;overflow:hidden;box-shadow:var(--shadow-card)}.fcq-account>div{padding:16px 18px;border-right:1px solid var(--border)}.fcq-account>div:last-child{border:0}.fcq-identity{display:flex;align-items:center;gap:12px}.fcq-identity>i{width:40px;height:40px;border-radius:12px;background:color-mix(in srgb,var(--frd-accent) 12%,var(--surface-1));color:var(--frd-accent);font-family:var(--mono,ui-monospace,monospace);font-style:normal;font-weight:750;display:grid;place-items:center}.fcq-identity strong,.fcq-identity span{display:block}.fcq-identity span{font-size:10px;color:var(--text-muted);margin-top:3px}.fcq-metric span,.fcq-metric small{display:block;color:var(--text-muted);font-size:9px}.fcq-metric strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:18px;margin:4px 0 2px}.fcq-metric strong.warn{color:#b45309}.fcq-layout{display:grid;grid-template-columns:290px minmax(0,1fr);gap:14px;margin-top:14px;align-items:start}.fcq-lanes,.fcq-main>section,.fcq-insights>section{border:1px solid var(--border);background:var(--surface-0);border-radius:14px;box-shadow:var(--shadow-card)}.fcq-lanes{overflow:hidden}.fcq-lanes>header,.fcq-actions>header{display:flex;justify-content:space-between;padding:13px 14px;border-bottom:1px solid var(--border);font-size:11px}.fcq-lanes>header span,.fcq-actions>header span{font-size:9px;color:var(--text-muted)}.fcq-lanes>button{display:flex;justify-content:space-between;gap:12px;width:100%;border:0;border-bottom:1px solid var(--border);background:transparent;color:inherit;text-align:left;padding:13px 14px;cursor:pointer}.fcq-lanes>button.active{background:var(--surface-1);box-shadow:inset 3px 0 var(--frd-accent)}.fcq-lanes button div:first-child b,.fcq-lanes button div:first-child span{display:block}.fcq-lanes button div:first-child b{font-family:var(--mono,ui-monospace,monospace);font-size:10px}.fcq-lanes button div:first-child span{font-size:9px;color:var(--text-muted);margin-top:4px}.fcq-lanes button div:nth-child(2){text-align:right}.fcq-lanes button div:nth-child(2) strong,.fcq-lanes button div:nth-child(2) small{display:block;white-space:nowrap}.fcq-lanes button div:nth-child(2) strong{font-family:var(--mono,ui-monospace,monospace);font-size:11px}.fcq-lanes button div:nth-child(2) small{font-size:8px;color:#047857;margin-top:4px}.fcq-lanes button div:nth-child(2) small.warm{color:#b45309}.fcq-lanes>.fcq-new-lane{display:block;text-align:center;border:0;color:var(--frd-accent);font-size:10px;font-weight:700}.fcq-main{min-width:0}.fcq-lane-head{display:flex;justify-content:space-between;align-items:end;padding:5px 4px 14px}.fcq-lane-head h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:25px;letter-spacing:-.02em;margin:4px 0 0}.fcq-lane-head button,.fcq-position-card button{border:0;background:var(--text-primary);color:var(--surface-0);padding:9px 13px;border-radius:9px;font-size:10px;font-weight:700}.fcq-insights{display:grid;grid-template-columns:1.8fr .9fr;gap:12px}.fcq-chart-card{padding:15px 17px}.fcq-chart-card header{display:flex;justify-content:space-between;align-items:flex-start}.fcq-chart-card header strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:20px;margin-top:3px}.fcq-chart-card header small{font-size:9px;color:var(--text-muted)}.fcq-chart{width:100%;height:115px;margin-top:4px;overflow:visible}.fcq-chart-card footer{display:flex;justify-content:space-between;color:var(--text-muted);font-family:var(--mono,ui-monospace,monospace);font-size:7px}.fcq-position-card{padding:16px;display:flex;flex-direction:column}.fcq-position-card>strong{font-family:var(--mono,ui-monospace,monospace);font-size:33px;color:var(--frd-accent);margin-top:8px}.fcq-position-card>p{font-size:10px;color:var(--text-muted);line-height:1.45;margin:3px 0 12px}.fcq-position-card>div{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:auto}.fcq-position-card>div span{font-size:8px;color:var(--text-muted)}.fcq-position-card>div b{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:11px;color:var(--text-primary);margin-top:2px}.fcq-position-card button{margin-top:12px;width:100%}.fcq-options{margin-top:12px;padding:16px}.fcq-options>header{display:flex;justify-content:space-between;align-items:end}.fcq-options h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:18px;margin:3px 0 0}.fcq-options header small{font-size:9px;color:var(--text-muted)}.fcq-options>div{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:12px}.fcq-options article{position:relative;border:1px solid var(--border);border-radius:10px;padding:12px;overflow:hidden}.fcq-options article.best{border-color:color-mix(in srgb,#2563eb 45%,var(--border))}.fcq-options article>i{position:absolute;left:0;top:0;bottom:0;width:3px}.fcq-options article div span,.fcq-options article div b,.fcq-options article div small{display:block}.fcq-options article div span{font-size:8px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.05em}.fcq-options article div b{font-size:11px;margin-top:2px}.fcq-options article div small{font-size:8px;color:var(--text-muted);margin-top:2px}.fcq-options article>strong{font-family:var(--mono,ui-monospace,monospace);font-size:17px;display:block;margin:11px 0 8px}.fcq-options article>button{border:0;background:none;color:var(--frd-accent);font-size:9px;font-weight:750;padding:0}.fcq-options article>button.quiet{color:var(--text-muted)}.fcq-actions{margin-top:12px;overflow:hidden}.fcq-action{display:grid;grid-template-columns:3px minmax(0,1fr) auto;gap:11px;align-items:center;padding:11px 14px;border-bottom:1px solid var(--border)}.fcq-action:last-child{border:0}.fcq-action>i{width:3px;height:30px;border-radius:3px}.fcq-action div b,.fcq-action div span{display:block}.fcq-action div b{font-size:10.5px}.fcq-action div span{font-size:9px;color:var(--text-muted);margin-top:2px}.fcq-action button{border:1px solid var(--border);background:var(--surface-0);color:var(--text-primary);border-radius:8px;padding:7px 9px;font-size:9px;font-weight:700;white-space:nowrap}
.fcq-lanes-head{align-items:center;gap:8px}.fcq-lanes-head>div b,.fcq-lanes-head>div span{display:block}.fcq-lanes-head>div span{margin-top:2px}.fcq-lanes-head select{max-width:112px;border:1px solid var(--border);border-radius:8px;background:var(--surface-1);color:inherit;padding:6px 7px;font-size:9px}.fcq-lane-head p{margin:4px 0 0;color:var(--text-muted);font-size:9px}.fcq-lanes button div:nth-child(2) em{display:block;font-style:normal;font-family:var(--mono,ui-monospace,monospace);font-size:7.5px;color:#047857;margin-top:3px}.fcq-economics{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid var(--border);background:var(--surface-0);border-radius:12px;overflow:hidden;margin-bottom:12px}.fcq-economics>div{padding:11px 13px;border-right:1px solid var(--border)}.fcq-economics>div:last-child{border:0}.fcq-economics .fcq-metric strong{font-size:15px}.fcq-economics .fcq-metric:nth-child(3) strong,.fcq-economics .fcq-metric:nth-child(4) strong{color:#047857}
.fcq-recent{border:1px solid var(--border);background:var(--surface-0);border-radius:14px;margin-top:12px;overflow:hidden;box-shadow:var(--shadow-card)}.fcq-recent>header{display:flex;justify-content:space-between;align-items:center;padding:12px 15px;border-bottom:1px solid var(--border)}.fcq-recent>header span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.fcq-recent>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:16px;margin:2px 0 0}.fcq-recent>header button{border:0;background:none;color:var(--frd-accent);font-size:9px;font-weight:700}.fcq-recent>div{display:grid;grid-template-columns:repeat(3,1fr)}.fcq-recent article{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:13px;align-items:center;padding:12px 14px;border-right:1px solid var(--border)}.fcq-recent article:last-child{border:0}.fcq-recent article div:first-child small,.fcq-recent article div:first-child b,.fcq-recent article div:first-child span{display:block}.fcq-recent article small,.fcq-recent article span{font-size:7.5px;color:var(--text-muted)}.fcq-recent article b{font-family:var(--mono,ui-monospace,monospace);font-size:9.5px;margin:3px 0}.fcq-recent article strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:10px;margin-top:3px}.fcq-recent article .profit strong{color:#047857}.fcq-recent article>button{grid-column:1/-1;border:0;background:var(--surface-1);color:var(--frd-accent);border-radius:7px;padding:7px;font-size:8.5px;font-weight:700}
@media(max-width:760px){.fcq-head{align-items:stretch;flex-direction:column}.fcq-head label{min-width:0}.fcq-account{grid-template-columns:1fr 1fr}.fcq-account>div{border-bottom:1px solid var(--border)}.fcq-identity{grid-column:1/-1}.fcq-recent>div{grid-template-columns:1fr}.fcq-recent article{border-right:0;border-bottom:1px solid var(--border)}.fcq-layout{grid-template-columns:1fr}.fcq-lanes{max-height:290px;overflow:auto}.fcq-economics{grid-template-columns:1fr 1fr}.fcq-economics>div{border-bottom:1px solid var(--border)}.fcq-insights{grid-template-columns:1fr}.fcq-options>header{align-items:flex-start;flex-direction:column;gap:4px}.fcq-options>div{grid-template-columns:1fr}.fcq-action{grid-template-columns:3px minmax(0,1fr)}.fcq-action button{grid-column:2;width:max-content}.fcq-lane-head{padding-inline:0}.fcq-chart{height:100px}}
`
